import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface LoadingSpinnerProps {
  text?: string;
  size?: 'sm' | 'md' | 'lg';
}

interface PageLoaderProps {
  pageName: string;
  subtitle?: string;
  loadingText?: string;
}

/**
 * Small loading spinner for inline use (e.g., inside modals, search results, buttons)
 * 
 * @example
 * // Inside search results
 * <LoadingSpinner text="Searching players..." size="sm" />
 * 
 * // In a button
 * <LoadingSpinner size="sm" />
 * 
 * // For saving states
 * <LoadingSpinner text="Saving roster..." size="sm" />
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  text = "Loading...", 
  size = 'md' 
}) => {
  const [showPinata, setShowPinata] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const sizeClasses = {
    sm: 'text-xl',
    md: 'text-3xl',
    lg: 'text-5xl'
  };

  const containerClasses = {
    sm: 'p-4',
    md: 'p-8',
    lg: 'p-12'
  };

  useEffect(() => {
    const timer1 = setTimeout(() => setShowPinata(true), 800);
    const timer2 = setTimeout(() => setShowConfetti(true), 1200);
    const timer3 = setInterval(() => {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 500);
    }, 3000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearInterval(timer3);
    };
  }, []);

  return (
    <div className={`flex flex-col items-center justify-center ${containerClasses[size]} relative`}>
      <div className="relative">
        {/* Football to Piñata Animation */}
        <AnimatePresence mode="wait">
          {!showPinata ? (
            <motion.div
              key="football"
              className={`${sizeClasses[size]}`}
              initial={{ scale: 1, rotate: 0 }}
              animate={{ 
                rotate: [0, 10, -10, 5, -5, 0],
                y: [0, -10, 0]
              }}
              exit={{ scale: 0, rotate: 90 }}
              transition={{ 
                rotate: { duration: 0.8, repeat: Infinity },
                y: { duration: 1.2, repeat: Infinity, ease: "easeInOut" },
                exit: { duration: 0.3 }
              }}
            >
              🏈
            </motion.div>
          ) : (
            <motion.div
              key="pinata"
              className={`${sizeClasses[size]}`}
              initial={{ scale: 0, rotate: -90 }}
              animate={{ 
                scale: 1, 
                rotate: 0,
                y: [0, -8, 0]
              }}
              transition={{ 
                scale: { duration: 0.4, ease: "backOut" },
                rotate: { duration: 0.4, ease: "backOut" },
                y: { duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }
              }}
            >
              🪅
            </motion.div>
          )}
        </AnimatePresence>

        {/* Confetti Burst */}
        <AnimatePresence>
          {showConfetti && (
            <>
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={`confetti-${i}`}
                  className="absolute top-1/2 left-1/2 pointer-events-none"
                  initial={{ 
                    x: 0, 
                    y: 0, 
                    scale: 0,
                    rotate: 0
                  }}
                  animate={{ 
                    x: (i % 2 === 0 ? 1 : -1) * (20 + i * 10),
                    y: -20 - i * 8,
                    scale: [0, 1, 0],
                    rotate: 360
                  }}
                  exit={{ opacity: 0 }}
                  transition={{ 
                    duration: 0.6,
                    ease: "easeOut",
                    delay: i * 0.05
                  }}
                >
                  <span 
                    className={`text-sm ${
                      i % 3 === 0 ? 'text-fiesta-yellow' : 
                      i % 3 === 1 ? 'text-fiesta-blue' : 'text-fiesta-pink'
                    }`}
                  >
                    {['✨', '🎉', '⭐', '💫', '🌟', '✨'][i]}
                  </span>
                </motion.div>
              ))}
            </>
          )}
        </AnimatePresence>
      </div>
      
      {text && (
        <motion.p 
          className="mt-6 text-gray-400 text-center font-medium"
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          {text}
        </motion.p>
      )}
    </div>
  );
};

/**
 * Full page loader for consistent page loading states
 * Use this when the entire page is loading, provides unified experience across all pages
 * 
 * @example
 * // Basic usage
 * if (loading) return <PageLoader pageName="Player Rankings" />
 * 
 * // With subtitle and custom loading text
 * if (loading) return (
 *   <PageLoader 
 *     pageName="Trade Calculator"
 *     subtitle="Evaluate your trades to see if you're getting fair value"
 *     loadingText="Loading player data for trade analysis..."
 *   />
 * )
 */
export const PageLoader: React.FC<PageLoaderProps> = ({ 
  pageName, 
  subtitle,
  loadingText
}) => {
  return (
    <div className="relative min-h-[65vh] bg-ink" aria-busy="true" aria-live="polite">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
        <div className="text-center">
          <h1 className="font-display text-3xl font-semibold text-white sm:text-4xl">
            {pageName}
          </h1>
          {subtitle ? (
            <p className="mx-auto mt-3 max-w-2xl text-base text-zinc-300">{subtitle}</p>
          ) : null}
          <p className="mt-3 text-sm text-zinc-500">{loadingText || 'Loading...'}</p>
        </div>
      </div>
    </div>
  );
};
